if (!customElements.get('scroll-gallery')) {
  class ScrollGallery extends HTMLElement {
    constructor() {
      super();

      this.scroller = this.querySelector('.js-scroll-gallery-scroller');
      this.viewport = this.querySelector('.js-scroll-gallery-viewport');
      this.track = this.querySelector('.js-scroll-gallery-track');
      this.intro = this.querySelector('.js-scroll-gallery-intro');
      this.cards = Array.from(this.querySelectorAll('.js-scroll-gallery-card'));
      this.counter = this.querySelector('.js-scroll-gallery-counter');
      this.prevButton = this.querySelector('.js-scroll-gallery-prev');
      this.nextButton = this.querySelector('.js-scroll-gallery-next');

      this.scrollLength = parseFloat(this.dataset.scrollLength) || 1;

      /* Browser drives the movement on the compositor -- immune to main-thread jank */
      this.supportsTimeline =
        window.CSS?.supports?.('animation-timeline', 'view()') &&
        CSS.supports('view-timeline-inset', '0px 0px');

      /* Layout cache -- filled in measure(), never read during animation */
      this.scrollerTop = 0;
      this.stickyOffset = 0;
      this.overflow = 0;
      this.distance = 0;
      this.trackPadding = 0;
      this.introFade = 1;

      /* Animation state */
      this.currentOffset = 0;
      this.targetOffset = 0;
      this.lastFrameTime = 0;
      this.frame = null;
      this.currentIndex = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onViewportScroll = this.onViewportScroll.bind(this);
      this.tick = this.tick.bind(this);
    }

    connectedCallback() {
      if (this.cards.length === 0) {
        return;
      }

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pinQuery = window.matchMedia('(min-width: 750px)');

      this.preloadImages();
      this.setup();

      this.motionQuery.addEventListener('change', this.onResize);
      this.pinQuery.addEventListener('change', this.onResize);
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('scroll', this.onViewportScroll, { passive: true });

      /* Content above the section (lazy images, apps) can move it -- re-measure then */
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(document.body);
      this.resizeObserver.observe(this.track);

      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.cards.indexOf(event.target.closest('.js-scroll-gallery-card'));

          if (index > -1) {
            this.goTo(index + 1, 'auto');
          }
        });
      }
    }

    disconnectedCallback() {
      this.motionQuery?.removeEventListener('change', this.onResize);
      this.pinQuery?.removeEventListener('change', this.onResize);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('scroll', this.onViewportScroll);
      this.resizeObserver?.disconnect();
      this.imageObserver?.disconnect();
      window.cancelAnimationFrame(this.frame);
      window.cancelAnimationFrame(this.counterFrame);
      window.cancelAnimationFrame(this.resizeFrame);
    }

    get isPinned() {
      return this.classList.contains('scroll-gallery--pinned');
    }

    get usesTimeline() {
      return this.classList.contains('scroll-gallery--timeline');
    }

    /* Decode card images before they slide in, so nothing loads mid-animation */
    preloadImages() {
      const images = this.querySelectorAll('.scroll-gallery__media img');

      images.forEach((img) => img.setAttribute('decoding', 'async'));

      this.imageObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) {
            return;
          }

          images.forEach((img) => img.setAttribute('loading', 'eager'));
          this.imageObserver.disconnect();
        },
        { rootMargin: '100% 0px' }
      );

      this.imageObserver.observe(this);
    }

    setup() {
      const shouldPin = this.pinQuery.matches && !this.motionQuery.matches;

      this.classList.toggle('scroll-gallery--pinned', shouldPin);
      this.classList.toggle('scroll-gallery--timeline', shouldPin && this.supportsTimeline);
      this.measure();
    }

    measure() {
      this.trackPadding = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;

      if (!this.isPinned) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
        this.style.removeProperty('--scroll-gallery-scroll-distance');
        this.track.style.removeProperty('transform');
        this.intro?.style.removeProperty('opacity');
        this.currentIndex = 0;
        this.onViewportScroll();
        return;
      }

      const lastCard = this.cards[this.cards.length - 1];
      const trackEndPadding = parseFloat(getComputedStyle(this.track).paddingInlineEnd) || 0;
      const trackWidth = lastCard.offsetLeft + lastCard.offsetWidth + trackEndPadding;

      this.stickyOffset =
        parseFloat(getComputedStyle(this).getPropertyValue('--scroll-gallery-sticky-offset')) || 0;
      this.overflow = Math.max(0, trackWidth - this.viewport.clientWidth);
      this.distance = this.overflow * this.scrollLength;
      /* Faded out by the time the first card has slid ~60% across the text, and always by the end */
      this.introFade = Math.max(Math.min((this.intro?.offsetWidth || 0) * 0.6, this.overflow), 1);

      /* Only touch the style when it changes -- it resizes the page */
      const distanceValue = `${Math.round(this.distance)}px`;

      if (this.style.getPropertyValue('--scroll-gallery-scroll-distance') !== distanceValue) {
        this.style.setProperty('--scroll-gallery-scroll-distance', distanceValue);
      }

      this.scrollerTop = this.scroller.getBoundingClientRect().top + window.scrollY;

      if (this.usesTimeline) {
        const fadeEnd = this.overflow > 0 ? Math.min((this.introFade / this.overflow) * 100, 100) : 100;

        this.style.setProperty('--scroll-gallery-overflow', `${Math.round(this.overflow)}px`);
        this.style.setProperty('--scroll-gallery-intro-fade-end', `${fadeEnd.toFixed(2)}%`);
        this.track.style.removeProperty('transform');
        this.intro?.style.removeProperty('opacity');
        this.intro?.style.removeProperty('visibility');
        this.setCurrentIndex(this.indexFromOffset(this.offsetForScroll(window.scrollY)));
        return;
      }

      /* Jump straight to the right spot after a re-measure, no easing */
      this.targetOffset = this.offsetForScroll(window.scrollY);
      this.currentOffset = this.targetOffset;
      this.render();
    }

    offsetForScroll(scrollY) {
      if (this.distance <= 0) {
        return 0;
      }

      const progress = (scrollY + this.stickyOffset - this.scrollerTop) / this.distance;

      return Math.min(Math.max(progress, 0), 1) * this.overflow;
    }

    onScroll() {
      if (!this.isPinned) {
        return;
      }

      this.targetOffset = this.offsetForScroll(window.scrollY);

      /* CSS moves the track; only the counter needs updating here */
      if (this.usesTimeline) {
        if (!this.counterFrame) {
          this.counterFrame = window.requestAnimationFrame(() => {
            this.counterFrame = null;
            this.setCurrentIndex(this.indexFromOffset(this.targetOffset));
          });
        }
        return;
      }

      if (!this.frame) {
        this.lastFrameTime = performance.now();
        this.frame = window.requestAnimationFrame(this.tick);
      }
    }

    /* Eases the track toward the scroll position so wheel steps glide instead of jump */
    tick(now) {
      const elapsed = Math.min(now - this.lastFrameTime, 64);
      const ease = 1 - Math.pow(1 - 0.16, elapsed / 16.67);

      this.lastFrameTime = now;
      this.currentOffset += (this.targetOffset - this.currentOffset) * ease;

      if (Math.abs(this.targetOffset - this.currentOffset) < 0.5) {
        this.currentOffset = this.targetOffset;
        this.frame = null;
      } else {
        this.frame = window.requestAnimationFrame(this.tick);
      }

      this.render();
    }

    /* Writes only -- no layout reads, so the browser never recalculates mid-frame */
    render() {
      const offset = this.currentOffset;

      this.track.style.transform = `translate3d(${-offset.toFixed(2)}px, 0, 0)`;

      if (this.intro) {
        const opacity = Math.min(Math.max(1 - offset / this.introFade, 0), 1);

        this.intro.style.opacity = opacity.toFixed(3);
        this.intro.style.visibility = opacity === 0 ? 'hidden' : '';
      }

      this.setCurrentIndex(this.indexFromOffset(offset));
    }

    /* Native scrolling fallback (mobile / reduced motion) */
    onViewportScroll() {
      if (this.isPinned) {
        return;
      }

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;

      this.setCurrentIndex(this.indexFromOffset(this.viewport.scrollLeft, maxScroll));
    }

    /* 1-based step from scroll progress, so the counter always reaches n/n at the end */
    indexFromOffset(offset, max = this.overflow) {
      if (max <= 0) {
        return 1;
      }

      const progress = Math.min(Math.max(offset / max, 0), 1);

      return Math.round(progress * (this.cards.length - 1)) + 1;
    }

    setCurrentIndex(index) {
      if (index === this.currentIndex) {
        return;
      }

      this.currentIndex = index;

      if (this.counter) {
        this.counter.textContent = `${index}/${this.cards.length}`;
      }

      if (this.prevButton) {
        this.prevButton.disabled = index <= 1;
      }

      if (this.nextButton) {
        this.nextButton.disabled = index >= this.cards.length;
      }
    }

    onResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.setup());
    }

    goTo(index, behavior) {
      const target = this.cards[Math.min(Math.max(index, 1), this.cards.length) - 1];

      if (!target) {
        return;
      }

      const scrollBehavior = behavior || (this.motionQuery.matches ? 'auto' : 'smooth');
      const offset = Math.max(0, target.offsetLeft - this.trackPadding);

      if (!this.isPinned) {
        this.viewport.scrollTo({ left: offset, behavior: scrollBehavior });
        return;
      }

      /* Same even steps as the counter */
      const steps = this.cards.length - 1;
      const progress = steps > 0 ? (Math.min(Math.max(index, 1), this.cards.length) - 1) / steps : 0;

      window.scrollTo({
        top: this.scrollerTop - this.stickyOffset + progress * this.distance,
        behavior: scrollBehavior
      });
    }
  }

  customElements.define('scroll-gallery', ScrollGallery);
}
